import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Sidebar from "../components/Sidebar";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Bell, Send, Users, User, Info, AlertCircle, BookOpen, Award, Sparkles } from "lucide-react";

export default function BroadcastCenter({ isInsideAdminLayout = false }) {
  const [role, setRole] = useState("teacher");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form State
  const [targetType, setTargetType] = useState("all_students");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [type, setType] = useState("promo");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const r = jwtDecode(token).role;
        if (r) setRole(r);
      } catch (e) {}
    }
    // Fetch targetable student list
    api.get("/notifications/students")
      .then(res => {
        setStudents(res.data || []);
      })
      .catch(err => console.error("Students fetch failure", err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error("Please enter both a Title and Description.");
      return;
    }
    if (targetType === "specific_user" && !selectedStudentId) {
      toast.error("Please select a recipient student.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        target_type: targetType,
        user_id: targetType === "specific_user" ? parseInt(selectedStudentId) : null,
        type,
        title,
        message
      };
      await api.post("/notifications/create", payload);
      toast.success("Broadcast dispatched successfully! 🚀");
      
      // Reset inputs
      setTitle("");
      setMessage("");
      setSelectedStudentId("");
    } catch (err) {
      toast.error("Dispatch failure.");
    } finally {
      setLoading(false);
    }
  };

  const renderInnerForm = () => (
    <div className="max-w-4xl mx-auto">
      <Toaster />
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-black mb-4 uppercase tracking-widest">
          <Sparkles size={14} /> Notification Dispatch
        </div>
        <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Broadcast Center</h1>
        <p className="text-slate-500 mt-2 font-semibold text-lg">Deliver direct messages, exam alerts, and global announcements instantly.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left: Broadcast Input Panel */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] p-8 md:p-10 shadow-xl shadow-slate-100/50 dark:shadow-none space-y-8">
            
            {/* Target Selection Toggle */}
            <div className="space-y-3">
              <label className="text-sm font-black text-slate-900 dark:text-slate-200 uppercase tracking-wider">Target Audience</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTargetType("all_students")}
                  className={`p-5 rounded-2xl border-2 flex flex-col items-center justify-center text-center gap-2 transition-all ${targetType === 'all_students' ? 'border-indigo-600 bg-indigo-50/30 text-indigo-600 dark:text-white dark:bg-indigo-950/20' : 'border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                >
                  <Users size={24} />
                  <span className="font-bold text-sm">All Students</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTargetType("specific_user")}
                  className={`p-5 rounded-2xl border-2 flex flex-col items-center justify-center text-center gap-2 transition-all ${targetType === 'specific_user' ? 'border-indigo-600 bg-indigo-50/30 text-indigo-600 dark:text-white dark:bg-indigo-950/20' : 'border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                >
                  <User size={24} />
                  <span className="font-bold text-sm">Specific Student</span>
                </button>
              </div>
            </div>

            {/* Dynamic Dropdown for single student */}
            {targetType === "specific_user" && (
              <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-wider">Select Recipient</label>
                <select 
                  value={selectedStudentId}
                  onChange={e=>setSelectedStudentId(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border dark:border-slate-800 rounded-xl px-4 py-3 font-semibold outline-none focus:ring-2 ring-indigo-600 transition-all"
                >
                  <option value="">-- Select a student --</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name} ({s.email})</option>
                  ))}
                </select>
              </motion.div>
            )}

            {/* Notification Type */}
            <div className="space-y-2">
              <label className="text-sm font-black text-slate-900 dark:text-slate-200 uppercase tracking-wider">Alert Type</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "promo", label: "Global Info", icon: Bell, col: "bg-blue-500" },
                  { id: "exam", label: "Exam Alert", icon: Award, col: "bg-rose-500" },
                  { id: "assignment", label: "Task Update", icon: BookOpen, col: "bg-orange-500" }
                ].map(t => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setType(t.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black border transition-all ${type === t.id ? 'border-slate-900 dark:border-white bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300'}`}
                  >
                    <t.icon size={14} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Text Inputs */}
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-black text-slate-900 dark:text-slate-200 uppercase tracking-wider">Broadcast Title</label>
                <input 
                  type="text"
                  placeholder="e.g., Important Class Rescheduled"
                  value={title}
                  onChange={e=>setTitle(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-indigo-500 dark:focus:border-indigo-500 rounded-2xl px-6 py-4 font-semibold outline-none transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-black text-slate-900 dark:text-slate-200 uppercase tracking-wider">Description / Payload</label>
                <textarea 
                  rows={5}
                  placeholder="Describe the context and steps required..."
                  value={message}
                  onChange={e=>setMessage(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-indigo-500 dark:focus:border-indigo-500 rounded-2xl px-6 py-4 font-medium outline-none transition-all resize-none leading-relaxed"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-base shadow-xl shadow-indigo-500/25 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:pointer-events-none"
            >
              {loading ? (
                <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={18} />
                  Push to Device Feeds
                </>
              )}
            </button>
          </form>
        </div>

        {/* Right: Live Preview Column */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-900 dark:bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 shadow-sm text-slate-200 sticky top-24">
            <div className="flex items-center gap-2 text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-8">
              <Info size={12} /> Device Live Feed Preview
            </div>
            
            <div className="relative bg-slate-950 border border-slate-800 rounded-3xl p-5 overflow-hidden shadow-inner">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 rounded-full" />
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-slate-900 text-indigo-400 border border-slate-800 flex-shrink-0">
                  {type === 'assignment' ? <BookOpen size={18} /> : type === 'exam' ? <Award size={18} /> : <Bell size={18} />}
                </div>
                <div className="flex-1 overflow-hidden">
                  <h4 className="font-black text-sm text-white truncate leading-tight">{title || "Insert Title..."}</h4>
                  <p className="text-slate-500 text-[10px] mt-1">Just Now • Local Time</p>
                  <p className="text-xs text-slate-400 mt-3 leading-relaxed break-words line-clamp-4">{message || "Message payload preview appears here in realtime as you type on the panel..."}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-start gap-3 bg-slate-800/50 border border-slate-800 p-5 rounded-2xl text-xs text-slate-400">
              <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
              <p className="leading-relaxed">Action cannot be reversed. Notifications sent using this tool are permanently stored in target feeds.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // DYNAMIC LAYOUT INJECTION
  if (isInsideAdminLayout) {
    return renderInnerForm();
  }

  return (
    <div className="flex min-h-screen bg-[#f8fafc] dark:bg-[#080a10]">
      <Sidebar role={role} />
      <main className="flex-1 p-6 md:p-10 lg:ml-64 pb-32">
        {renderInnerForm()}
      </main>
    </div>
  );
}
